const API_BASE_URL = (import.meta.env.VITE_AXON_API_BASE_URL || "https://api.axonleads.ru").replace(/\/$/, "");

export class LeadApiError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = "LeadApiError";
    Object.assign(this, details);
  }
}

async function leadRequest(idToken, path, options = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method || "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      cache: "no-store",
      credentials: "omit",
      redirect: "error",
      referrerPolicy: "no-referrer",
    });
  } catch {
    throw new LeadApiError("Нет связи с потоком лидов. Проверьте интернет и попробуйте ещё раз.");
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error || {};
    throw new LeadApiError(
      typeof error.message === "string" ? error.message : "Не получилось обновить поиск.",
      {
        status: response.status,
        code: error.code,
        resource: error.resource,
        usage: error.usage,
        limit: error.limit,
        recoveryAction: error.recoveryAction,
      },
    );
  }
  return payload;
}

export function fetchLeadProfile(idToken) {
  return leadRequest(idToken, "/v1/lead-profile");
}

export function fetchLeadSources(idToken) {
  return leadRequest(idToken, "/v1/lead-sources");
}

export function fetchLeadQuality(idToken) {
  return leadRequest(idToken, "/v1/lead-quality");
}

export function resetLeadQuality(idToken) {
  return leadRequest(idToken, "/v1/lead-quality/reset", { method: "POST" });
}

export function previewLeadProfile(idToken, profile) {
  return leadRequest(idToken, "/v1/lead-profile/preview?limit=12", {
    method: "POST",
    body: profile,
  });
}

export function saveLeadProfile(idToken, profile) {
  return leadRequest(idToken, "/v1/lead-profile", { method: "PUT", body: profile });
}

export function saveOnboardingExampleDecisions(idToken, payload) {
  return leadRequest(idToken, "/v1/onboarding/example-decisions", {
    method: "POST",
    body: payload,
  });
}

export function fetchLeadInbox(idToken, state = "new", offset = 0) {
  const params = new URLSearchParams({ state, offset: String(offset), limit: "30" });
  return leadRequest(idToken, `/v1/leads?${params.toString()}`);
}

export function fetchLeadDetail(idToken, assignmentId) {
  return leadRequest(idToken, `/v1/leads/${encodeURIComponent(assignmentId)}`);
}

export function fetchLeadSourceHistory(idToken) {
  return leadRequest(idToken, "/v1/leads/source-history");
}

export function refreshLeadAssignments(idToken) {
  return leadRequest(idToken, "/v1/leads/refresh", { method: "POST" });
}

export function recordLeadFeedback(idToken, assignmentId, verdict, reasonCode = null) {
  return leadRequest(idToken, `/v1/leads/${assignmentId}/feedback`, {
    method: "POST",
    body: { verdict, reasonCode },
  });
}

export function undoLeadFeedback(idToken, assignmentId, feedbackId) {
  return leadRequest(idToken, `/v1/leads/${assignmentId}/feedback/${feedbackId}/undo`, {
    method: "POST",
  });
}

export function recordLeadAction(idToken, assignmentId, action) {
  return leadRequest(idToken, `/v1/leads/${assignmentId}/actions`, {
    method: "POST",
    body: { action },
  });
}

export function recordLeadOutcome(idToken, assignmentId, outcome) {
  return leadRequest(idToken, `/v1/leads/${assignmentId}/outcomes`, {
    method: "POST",
    body: { outcome },
  });
}

export function generateReplyDraft(
  idToken,
  assignmentId,
  input,
  idempotencyKey,
) {
  return leadRequest(idToken, `/v1/leads/assignments/${assignmentId}/reply-drafts`, {
    method: "POST",
    body: input,
    headers: { "Idempotency-Key": idempotencyKey },
  });
}
